import React, { useEffect, useState, useCallback } from 'react'
import StatusPill from '../components/StatusPill'
import { useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { fmtDate, hl, generateCustomerReport, isGeneralJob, isSuspensionJob, JOB_FINANCIAL_SELECT, JOB_PAYMENT_OPTIONS, JOB_STATUS_OPTIONS, jobFinancials, jobHasApprovedQuote, jobModuleLabel, jobPaymentMeta, jobProgressMeta, bookingJobSummary, bookingComponentDraft, jobServiceScopeSummary, jobStatusMeta, normaliseJobStatus } from '../lib/utils'
import { generalServices, suspensionServices } from '../lib/serviceMenu'
import { newestJobFirst, workshopRowsResult } from '../lib/customerDirectory'
import ComponentModal from '../components/ComponentModal'
import GeneralJobModal from '../components/GeneralJobModal'
import JobModal from '../components/JobModal'
import NewJobIntakeModal from '../components/NewJobIntakeModal'
import ViewJobModal from '../components/ViewJobModal'
import LegacyHistoricJobModal from '../components/LegacyHistoricJobModal'
import { historicalOriginalCustomer } from '../lib/legacyHistoricJobImport'
import { historicalJobSearch, loadHistoricJobRecords } from '../lib/legacyHistoricJobs'

export default function Jobs() {
  const { workshopId, workshop, profile } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [customers, setCustomers] = useState([])
  const [bikes, setBikes] = useState([])
  const [components, setComponents] = useState([])
  const [jobs, setJobs] = useState([])
  const [historicRecords, setHistoricRecords] = useState([])
  const [historicLoadError, setHistoricLoadError] = useState('')
  const [historicLoading, setHistoricLoading] = useState(false)
  const [historicMode, setHistoricMode] = useState(false)
  const [viewHistoricId, setViewHistoricId] = useState(null)
  const [services, setServices] = useState([])
  const [generalJobServices, setGeneralJobServices] = useState([])
  const [loading, setLoading] = useState(true)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('active')
  const [dateFilter, setDateFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [techFilter, setTechFilter] = useState('all')
  const [paymentFilter, setPaymentFilter] = useState('all')
  const [showJobModal, setShowJobModal] = useState(false)
  const [showGeneralJobModal, setShowGeneralJobModal] = useState(false)
  const [componentLinkError, setComponentLinkError] = useState(null)
  const [compModal, setCompModal] = useState(null)
  const [editJob, setEditJob] = useState(null)   // job object for editing
  const [viewJobId, setViewJobId] = useState(null)

  const load = useCallback(async () => {
    if (!workshopId) return
    setLoadError('')
    const [c, b, co, j, j2] = await Promise.all([
      workshopRowsResult(supabase, 'customers', workshopId),
      workshopRowsResult(supabase, 'bikes', workshopId),
      workshopRowsResult(supabase, 'components', workshopId),
      workshopRowsResult(supabase, 'jobs', workshopId, JOB_FINANCIAL_SELECT, newestJobFirst),
      supabase.from('services').select('*').eq('workshop_id', workshopId).order('sort_order'),
    ])
    const readError = [c, b, co, j, j2].find(result => result?.error)?.error
    if (readError) {
      setLoadError(readError.message || 'Could not load workshop jobs.')
      setLoading(false)
      return
    }
    setCustomers(c.data || [])
    setBikes(b.data || [])
    setComponents(co.data || [])
    setJobs(j.data || [])
    const serviceRows = j2?.data || []
    setServices(suspensionServices(serviceRows))
    setGeneralJobServices(generalServices(serviceRows))
    setHasLoaded(true)
    setLoading(false)
  }, [workshopId])

  const loadHistory = useCallback(async () => {
    if (!workshopId) return
    setHistoricLoading(true)
    setHistoricLoadError('')
    try {
      setHistoricRecords(await loadHistoricJobRecords(supabase, workshopId))
    } catch (error) {
      setHistoricLoadError(error.message || 'Historical jobs could not be loaded.')
    } finally {
      setHistoricLoading(false)
    }
  }, [workshopId])

  useEffect(() => { load() }, [load])
  useEffect(() => { loadHistory() }, [loadHistory])

  useEffect(() => {
    const requestedJobId = searchParams.get('job')
    if (!requestedJobId) {
      setComponentLinkError(null)
      return
    }
    if (!jobs.length) return
    if (jobs.some(job => job.id === requestedJobId)) {
      setComponentLinkError(null)
      setViewJobId(requestedJobId)
      setStatusFilter('all')
    } else {
      setComponentLinkError('That job tag was not found, or you do not have access to it.')
    }
  }, [searchParams, jobs])

  function clearJobParam() {
    if (searchParams.has('job')) {
      const next = new URLSearchParams(searchParams)
      next.delete('job')
      setSearchParams(next, { replace: true })
    }
  }

  function closeViewJob() {
    setViewJobId(null)
    clearJobParam()
  }

  function editViewJob(job) {
    setViewJobId(null)
    clearJobParam()
    setEditJob(job)
  }

  function createComponentFromViewJob(job) {
    setViewJobId(null)
    clearJobParam()
    createComponentFromJob(job)
  }

  const q = search.trim().toLowerCase()
  const visibleHistoric = historicRecords.filter(record => historicalJobSearch(record, q))
  const linkedHistoricCount = historicRecords.filter(record => record.customer_id).length
  const todayStr = new Date().toISOString().slice(0, 10)
  const activeStatuses = new Set([
    'awaiting_customer_item',
    'received',
    'estimate_sent',
    'awaiting_parts',
    'ready_for_collection',
  ])
  const technicianOptions = [...new Set(jobs.map(j => String(j.technician || '').trim()).filter(Boolean))].sort()

  function addDays(dateStr, amount) {
    const d = new Date(`${dateStr}T12:00:00`)
    d.setDate(d.getDate() + amount)
    return d.toISOString().slice(0, 10)
  }

  const searched = jobs.filter(j => {
    if (!q) return true
    const comp = components.find(c => c.id === j.component_id)
    const cust = customers.find(c => c.id === j.customer_id)
    const summary = [jobServiceScopeSummary(j), bookingJobSummary(j)].filter(Boolean).join(' ')
    const status = jobStatusMeta(j)
    const payment = jobPaymentMeta(j)
    return (comp?.serial || '').toLowerCase().includes(q)
      || (j.job_number || '').toLowerCase().includes(q)
      || (j.physical_location || '').toLowerCase().includes(q)
      || summary.toLowerCase().includes(q)
      || status.label.toLowerCase().includes(q)
      || payment.label.toLowerCase().includes(q)
      || (cust ? `${cust.first_name} ${cust.last_name}`.toLowerCase().includes(q) : false)
  })
  const filtered = searched.filter(j => {
    const comp = components.find(c => c.id === j.component_id)
    const status = normaliseJobStatus(j.status)
    const payment = jobPaymentMeta(j)
    const dueOrDate = j.due_date || j.date

    if (statusFilter === 'quote_approved' && !jobHasApprovedQuote(j)) return false
    if (statusFilter === 'active' && !activeStatuses.has(status)) return false
    if (statusFilter === 'needs_component' && (!isSuspensionJob(j) || j.component_id)) return false
    if (!['all', 'active', 'needs_component', 'quote_approved'].includes(statusFilter) && status !== statusFilter) return false

    if (typeFilter === 'unassigned' && (!isSuspensionJob(j) || j.component_id)) return false
    if (!['all', 'unassigned'].includes(typeFilter) && (!isSuspensionJob(j) || comp?.type !== typeFilter)) return false

    if (techFilter === 'unassigned' && j.technician) return false
    if (!['all', 'unassigned'].includes(techFilter) && j.technician !== techFilter) return false

    if (paymentFilter !== 'all' && payment.key !== paymentFilter) return false

    if (dateFilter === 'today' && j.date !== todayStr) return false
    if (dateFilter === 'next7' && (!dueOrDate || dueOrDate < todayStr || dueOrDate > addDays(todayStr, 7))) return false
    if (dateFilter === 'next14' && (!dueOrDate || dueOrDate < todayStr || dueOrDate > addDays(todayStr, 14))) return false
    if (dateFilter === 'overdue' && (!j.due_date || j.due_date >= todayStr || !activeStatuses.has(status))) return false

    return true
  })
  const hasFilters = statusFilter !== 'active' || dateFilter !== 'all' || typeFilter !== 'all' || techFilter !== 'all' || paymentFilter !== 'all'
  const approvedQuoteJobs = jobs
    .filter(jobHasApprovedQuote)
    .filter(j => !['completed', 'cancelled'].includes(normaliseJobStatus(j.status)))
  const waitingPaymentJobs = jobs.filter(j => jobPaymentMeta(j).key === 'requested')

  function resetFilters() {
    setStatusFilter('active')
    setDateFilter('all')
    setTypeFilter('all')
    setTechFilter('all')
    setPaymentFilter('all')
  }

  const viewJob = jobs.find(j => j.id === viewJobId)
  const viewComp = viewJob ? components.find(c => c.id === viewJob.component_id) : null
  const viewCust = viewJob ? customers.find(c => c.id === viewJob.customer_id) : null
  const viewBike = viewJob ? bikes.find(b => b.id === viewJob.bike_id) : null
  const generalWorkshopEnabled = workshop?.general_workshop_enabled === true

  function createComponentFromJob(job) {
    if (!isSuspensionJob(job)) {
      setComponentLinkError('General Workshop jobs do not use the suspension component workflow yet.')
      return
    }
    setComponentLinkError(null)
    setCompModal({
      customerId: job.customer_id,
      jobId: job.id,
      prefillData: bookingComponentDraft(job),
    })
  }

  function generalJobValueLabel(job, financials) {
    const pricingMode = String(job?.pricing_mode || 'fixed').toLowerCase()
    const priceStatus = String(job?.price_status || 'final').toLowerCase()
    const amount = Number(financials?.totalCost ?? job?.total_charged ?? job?.labour_charge ?? 0)
    const amountLabel = amount >= 0 && (job?.total_charged != null || amount > 0) ? `GBP ${amount.toFixed(2)}` : ''
    if (pricingMode === 'quote_required' || priceStatus === 'quote_required') return amount > 0 ? `Quote required - ${amountLabel} known` : 'Quote required'
    if (pricingMode === 'from' || priceStatus === 'estimate') return amountLabel ? `Estimated total ${amountLabel}` : 'Estimate pending'
    return amountLabel || 'No price recorded'
  }

  async function handleComponentSaved(component) {
    const jobId = compModal?.jobId
    setComponentLinkError(null)

    if (jobId && component?.id) {
      const { error } = await supabase
        .from('jobs')
        .update({ component_id: component.id })
        .eq('id', jobId)
      if (error) setComponentLinkError(error.message)
    }

    setCompModal(null)
    await load()
  }

  if (loading) return <div className="empty"><span className="spinner" style={{ width: 20, height: 20 }} /></div>
  if (loadError && !hasLoaded) return (
    <div className="jobs-refresh-page">
      <div className="page-head jobs-refresh-head">
        <div>
          <div className="refresh-kicker">Workshop records</div>
          <div className="page-title">Jobs</div>
          <div className="text-muted fs12 mt4">Search workflow, scheduling and payment state in one place.</div>
        </div>
      </div>

      <div className="alert alert-error jobs-refresh-load-error" role="alert">
        <span>{loadError}</span>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setLoading(true); load() }}>Try again</button>
      </div>
    </div>
  )

  return (
    <div className="jobs-refresh-page">
      <div className="page-head jobs-refresh-head">
        <div>
          <div className="refresh-kicker">Workshop records</div>
          <div className="page-title">Jobs</div>
          <div className="text-muted fs12 mt4">Search workflow, scheduling and payment state in one place.</div>
        </div>
        <div className="page-actions">
          {generalWorkshopEnabled && <button className="btn btn-ghost" onClick={() => setShowGeneralJobModal(true)}>+ General Job</button>}
          <button className="btn btn-primary" onClick={() => setShowJobModal(true)}>+ Suspension Job</button>
        </div>
      </div>

      <div className="action-row" role="tablist" aria-label="Job views">
        <button type="button" role="tab" aria-selected={!historicMode}
          className={`btn ${historicMode ? 'btn-ghost' : 'btn-primary'}`}
          onClick={() => { setHistoricMode(false); setSearch('') }}>Service Jobs ({jobs.length})</button>
        <button type="button" role="tab" aria-selected={historicMode}
          className={`btn ${historicMode ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => { setHistoricMode(true); setSearch('') }}>
          Historical Jobs ({historicRecords.length})</button>
      </div>

      {loadError && (
        <div className="alert alert-error jobs-refresh-load-error" role="alert">
          <span>{loadError} The open job and unsaved entries have been kept.</span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={load}>Try again</button>
        </div>
      )}

      {historicMode ? <section className="jobs-historic-view" aria-label="Historical jobs">
        {historicLoadError && <div className="alert alert-error" role="alert">
          Historical jobs could not be loaded: {historicLoadError}
          <button type="button" className="btn btn-ghost btn-sm" onClick={loadHistory}>Retry history</button>
        </div>}
        {historicLoading && <div className="text-muted fs12">Loading historical jobs...</div>}
        <div className="search-wrap jobs-refresh-search">
          <input value={search} onChange={event => setSearch(event.target.value)}
            placeholder="Search source ID, customer, bike, mechanic or invoice facts" />
        </div>
        <div className="text-muted fs12 mt4">Legacy / previous operating system: {historicRecords.length} references,
          {' '}{linkedHistoricCount} linked, {historicRecords.length - linkedHistoricCount} unlinked.
          These do not count as active jobs.</div>
        {!visibleHistoric.length && !historicLoading && !historicLoadError ? <div className="empty"><p>{q
          ? 'No historical jobs match this search.' : 'No historical jobs have been imported.'}</p></div>
          : <div className="jobs-historic-list">{visibleHistoric.map(record => {
            const facts = record.facts || {}
            const customer = customers.find(row => row.id === record.customer_id)
            return <button type="button" key={record.id} className="jobs-historic-row"
              onClick={() => setViewHistoricId(record.id)}>
              <span><span className="job-status-chip job-status-muted">Legacy / previous operating system</span>
                <strong className="mono">{record.source_reference}</strong>
                <span>{historicalOriginalCustomer(facts) || 'Customer not supplied'}</span></span>
              <span>{facts.bikes || 'Bike not supplied'}</span>
              <span>{facts.created || facts.scheduled || 'Date not supplied'}
                {facts.status ? ` / ${facts.status}` : ''}{facts.invoice_number ? ` / ${facts.invoice_number}` : ''}</span>
              <span>{customer ? `Linked: ${customer.first_name} ${customer.last_name}` : 'Unlinked'}</span>
              <span>{profile?.role === 'owner' && !record.customer_id
                ? 'Review customer link' : 'Open historical detail'}</span>
            </button>
          })}</div>}
      </section> : <>
      <div className="search-wrap jobs-refresh-search">
        <span className="search-icon" aria-hidden="true">🔍</span>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by serial, customer or booking details…" />
        {search && <button type="button" className="search-clear" onClick={() => setSearch('')} aria-label="Clear job search">×</button>}
      </div>

      <div className="card jobs-refresh-filters">
        <div className="jobs-refresh-filter-grid">
          <div className="field">
            <label>Status</label>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="active">Active jobs</option>
              <option value="all">All jobs</option>
              <option value="needs_component">Needs component</option>
              <option value="quote_approved">Quote approved</option>
              {JOB_STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Date / Due</label>
            <select value={dateFilter} onChange={e => setDateFilter(e.target.value)}>
              <option value="all">Any date</option>
              <option value="today">Today</option>
              <option value="next7">Next 7 days</option>
              <option value="next14">Next 14 days</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>
          <div className="field">
            <label>Component</label>
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
              <option value="all">Any component</option>
              <option value="fork">Fork</option>
              <option value="shock">Shock</option>
              <option value="dropper">Dropper</option>
              <option value="brake">Brake</option>
              <option value="other">Other</option>
              <option value="unassigned">No component</option>
            </select>
          </div>
          <div className="field">
            <label>Technician</label>
            <select value={techFilter} onChange={e => setTechFilter(e.target.value)}>
              <option value="all">Any technician</option>
              <option value="unassigned">Unassigned</option>
              {technicianOptions.map(name => <option key={name} value={name}>{name}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Payment</label>
            <select value={paymentFilter} onChange={e => setPaymentFilter(e.target.value)}>
              {JOB_PAYMENT_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
        </div>
        <div className="jobs-refresh-filter-summary">
          <div className="fs12 text-muted">
            Showing <span className="text-white fw7">{filtered.length}</span> of {jobs.length} job{jobs.length !== 1 ? 's' : ''}
            {waitingPaymentJobs.length > 0 && <span> - {waitingPaymentJobs.length} waiting payment</span>}
          </div>
          {hasFilters && <button className="btn btn-ghost btn-sm" onClick={resetFilters}>Reset filters</button>}
        </div>
      </div>

      {componentLinkError && <div className="alert alert-error">{componentLinkError}</div>}
      {approvedQuoteJobs.length > 0 && (
        <div className="alert alert-success" style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>{approvedQuoteJobs.length} quote{approvedQuoteJobs.length !== 1 ? 's' : ''} accepted - ready to continue.</span>
          <button className="btn btn-green btn-sm" onClick={() => setStatusFilter('quote_approved')}>Show accepted quotes</button>
        </div>
      )}
      {q && <div className="result-count">{filtered.length} of {searched.length} matching job{searched.length !== 1 ? 's' : ''} shown</div>}

      {!filtered.length ? (
        <div className="empty jobs-refresh-empty">
          <div className="empty-icon">{q ? '🔍' : '🔧'}</div>
          <p>{q || hasFilters ? 'No jobs match the current search and filters.' : 'No service jobs yet.'}</p>
          {(q || hasFilters) && <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); resetFilters() }}>Clear search and filters</button>}
        </div>
      ) : (
        <section className="card jobs-refresh-list" aria-label="Service jobs">
          <div className="jobs-refresh-columns" aria-hidden="true">
            <span>Customer / job</span><span>Workflow</span><span>Schedule</span><span>Payment / value</span><span>Actions</span>
          </div>
          {filtered.map(j => {
                const comp = components.find(c => c.id === j.component_id)
                const cust = customers.find(c => c.id === j.customer_id)
                const generalJob = isGeneralJob(j)
                const summary = bookingJobSummary(j)
                const selectedServices = jobServiceScopeSummary(j)
                const status = jobStatusMeta(j)
                const progress = jobProgressMeta(j).filter(item => item.key !== 'payment')
                const payment = jobPaymentMeta(j)
                const hasAcceptedQuote = jobHasApprovedQuote(j)
                const financials = jobFinancials(j)
                const jobTitle = comp
                  ? [comp.year, comp.model].filter(Boolean).join(' ') || 'Component job'
                  : generalJob ? 'General Workshop job' : 'Booking job'
                return (
                  <article className="jobs-refresh-row" key={j.id}>
                    <div className="jobs-refresh-identity">
                      <div className="job-customer-name">{cust ? `${cust.first_name} ${cust.last_name}` : 'Unknown customer'}</div>
                      <div className="jobs-refresh-idline">
                        {j.job_number && <span className="mono text-faint">{j.job_number}</span>}
                        <span className={`job-status-chip job-status-${generalJob ? 'blue' : 'muted'}`}>{jobModuleLabel(j)}</span>
                      </div>
                      <div className="jobs-refresh-equipment">{jobTitle}</div>
                      {selectedServices && <div className="jobs-refresh-service">{selectedServices}</div>}
                      {!comp && <div className="jobs-refresh-muted">{summary || (generalJob ? 'General workshop job' : 'No component linked yet')}</div>}
                      {comp?.serial && <div className="mono jobs-refresh-muted" dangerouslySetInnerHTML={{ __html: hl(comp.serial, q) }} />}
                      {j.physical_location && <div className="jobs-refresh-muted">Location: {j.physical_location}</div>}
                    </div>
                    <div className="jobs-refresh-workflow" data-label="Workflow">
                      <div className="jobs-refresh-badges">
                        <StatusPill tone={status.tone}>{status.label}</StatusPill>
                        {hasAcceptedQuote && (
                          <StatusPill tone="green">Quote accepted</StatusPill>
                        )}
                      </div>
                      <div className="jobs-refresh-badges">
                        {progress.map(item => (
                          <StatusPill key={item.key} tone={item.tone}>
                            {item.label}: {item.value}
                          </StatusPill>
                        ))}
                      </div>
                    </div>
                    <div className="jobs-refresh-schedule" data-label="Schedule">
                      <strong>{fmtDate(j.date)}</strong>
                      {j.due_date && j.due_date !== j.date && <span>Due {fmtDate(j.due_date)}</span>}
                      <span>{j.technician || 'Unassigned'}</span>
                    </div>
                    <div className="jobs-refresh-payment" data-label="Payment / value">
                      <StatusPill
                        tone={payment.tone}
                      >
                        {payment.label}
                      </StatusPill>
                      {j.payment_requested_at && payment.key !== 'paid' && (
                        <div className="fs11 text-muted" style={{ marginTop: 6 }}>Requested {fmtDate(j.payment_requested_at.slice(0, 10))}</div>
                      )}
                      {j.payment_refresh_required && (
                        <div className="fs11 text-yellow" style={{ marginTop: 6 }}>New payment link needed</div>
                      )}
                      {j.payment_paid_at && (
                        <div className="fs11 text-green" style={{ marginTop: 6 }}>Paid {fmtDate(j.payment_paid_at.slice(0, 10))}</div>
                      )}
                      {j.payment_reminder_sent_at && <span>Reminder {fmtDate(j.payment_reminder_sent_at.slice(0, 10))}</span>}
                      <strong>{generalJob ? generalJobValueLabel(j, financials) : `GBP ${financials.totalCost.toFixed(2)}`}</strong>
                      {generalJob && <span>{String(j.price_status || 'final').replace(/_/g, ' ')}</span>}
                    </div>
                    <div className="jobs-refresh-actions" data-label="Actions">
                      <button className="btn btn-primary btn-sm" onClick={() => setEditJob(j)}>Open job</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setViewJobId(j.id)}>View summary</button>
                      {!generalJob && !comp && cust && (
                        <button className="btn btn-green btn-sm" onClick={() => createComponentFromJob(j)}>Add component</button>
                      )}
                      {generalJob && (
                        <button className="btn btn-ghost btn-sm" onClick={() => setViewJobId(j.id)}>Report</button>
                      )}
                      {!generalJob && comp && (
                        <button className="btn btn-ghost btn-sm" onClick={() => generateCustomerReport(j, cust, comp, workshop)}>Report</button>
                      )}
                    </div>
                  </article>
                )
          })}
        </section>
      )}

      </>}

      {viewHistoricId && historicRecords.find(record => record.id === viewHistoricId) &&
        <LegacyHistoricJobModal record={historicRecords.find(record => record.id === viewHistoricId)}
          customers={customers} owner={profile?.role === 'owner'}
          onClose={() => setViewHistoricId(null)}
          onLinked={updated => setHistoricRecords(current => current.map(record =>
            record.id === updated.id ? updated : record))} />}

      {/* New job modal */}
      {showJobModal && (
        <NewJobIntakeModal
          userId={profile?.id}
          workshopId={workshopId}
          customers={customers}
          bikes={bikes}
          components={components}
          services={services}
          returnLabel="Back to Jobs"
          onClose={() => setShowJobModal(false)}
          onSaved={(job) => { setShowJobModal(false); if (job?.id) setViewJobId(job.id); load() }}
        />
      )}

      {showGeneralJobModal && (
        <GeneralJobModal
          workshopId={workshopId}
          customers={customers}
          bikes={bikes}
          generalServices={generalJobServices}
          canCreateGeneralJob={generalWorkshopEnabled}
          returnLabel="Back to Jobs"
          onClose={() => setShowGeneralJobModal(false)}
          onSaved={(job) => { setShowGeneralJobModal(false); if (job?.id) setViewJobId(job.id); load() }}
        />
      )}

      {compModal && (
        <ComponentModal
          workshopId={workshopId}
          customerId={compModal.customerId}
          prefillData={compModal.prefillData}
          onClose={() => setCompModal(null)}
          onSaved={handleComponentSaved}
          onDeleted={() => { setCompModal(null); load() }}
        />
      )}

      {/* Edit job modal */}
      {editJob && (
        <JobModal
          workshopId={workshopId}
          customers={customers}
          bikes={bikes}
          components={components}
          services={services}
          generalServices={generalJobServices}
          initialData={editJob}
          returnLabel="Back to Jobs"
          onClose={() => setEditJob(null)}
          onChanged={load}
          onSaved={savedJob => { setEditJob(current => current?.id === savedJob?.id ? { ...current, ...savedJob } : current); load() }}
          onDeleted={() => { setEditJob(null); load() }}
        />
      )}

      {viewJobId && viewJob && (
        <ViewJobModal
          job={viewJob}
          customer={viewCust}
          component={viewComp}
          bike={viewBike}
          workshop={workshop}
          services={services}
          generalServices={generalJobServices}
          returnLabel="Back to Jobs"
          onClose={closeViewJob}
          onEdit={() => editViewJob(viewJob)}
          onChanged={load}
          onCreateComponent={isSuspensionJob(viewJob) && !viewComp && viewCust ? () => createComponentFromViewJob(viewJob) : null}
        />
      )}
    </div>
  )
}
